import { LogLevel, Participant, Room, RoomConnectOptions, setLogLevel } from 'livekit-client';

import { VideoLog, VideoLogContext } from '@features/video/lib/log';

const STUN = { urls: 'stun:stun.l.google.com:19302' };

// Внутренний журнал LiveKit по умолчанию info и засоряет консоль: оставляем предупреждения
setLogLevel(LogLevel.warn);

// Ответ token.php: билет обменивается на токен LiveKit и адрес сервера
export type TokenResponse = {
  token: string;
  url: string;
  room: string;
  role: 'host' | 'guest';
  waiting?: boolean;
  turn?: { urls: string | string[]; username: string; credential: string };
  error?: string;
};

// Метаданные участника LiveKit: роль и userId ставит token.php, privateWith — сам судья
export type ParticipantMeta = {
  role?: 'host' | 'guest' | 'waiting';
  uid?: string;
  privateWith?: string;
};

export async function exchangeTicket(
  server: string,
  ticket: string,
  log?: VideoLog,
): Promise<TokenResponse> {
  let data: TokenResponse;
  try {
    const res = await fetch(`${serverBase(server)}/token.php`, {
      method: 'POST',
      body: new URLSearchParams({ ticket }),
    });
    data = await res.json();
  } catch (e) {
    log?.error('token_failed', e, { server });
    throw new Error('Сервер видеосвязи недоступен');
  }
  if (data.error) {
    log?.error('token_rejected', new Error(data.error), { server });
    throw new Error(data.error);
  }
  return data;
}

// Подключение в два прохода: обычный с коротким бюджетом, затем только через TURN
// (relay) для сетей с закрытым UDP. Каждому проходу — своя комната
export async function connectRoom(
  createRoom: () => Room,
  tokenData: TokenResponse,
  directTimeoutMs: number,
  log?: VideoLog,
): Promise<Room> {
  const passes = [{ relay: false, timeout: directTimeoutMs }];
  if (tokenData.turn) passes.push({ relay: true, timeout: 0 });

  let lastError: unknown = null;
  // что было на каждом проходе — в журнал при полном провале
  const tried: VideoLogContext[] = [];
  for (const pass of passes) {
    const startedAt = Date.now();
    const name = pass.relay ? 'relay' : 'direct';
    const room = createRoom();
    const opts: RoomConnectOptions = { autoSubscribe: false };
    if (tokenData.turn) {
      opts.rtcConfig = {
        iceServers: [STUN, tokenData.turn],
        iceTransportPolicy: pass.relay ? 'relay' : undefined,
      };
    }
    if (pass.timeout) opts.peerConnectionTimeout = pass.timeout;
    try {
      await room.connect(tokenData.url, tokenData.token, opts);
      log?.event('connected', {
        pass: name,
        durationMs: Date.now() - startedAt,
        turn: !!tokenData.turn,
      });
      return room;
    } catch (e) {
      lastError = e;
      tried.push({ pass: name, durationMs: Date.now() - startedAt, reason: describeError(e) });
      room.disconnect();
    }
  }
  const error = lastError ?? new Error('Не удалось подключиться к серверу');
  log?.error('connect_failed', error, { passes: tried });
  throw error;
}

export function participantMeta(p: Participant): ParticipantMeta {
  return parseMeta(p.metadata);
}

export function parseMeta(metadata?: string): ParticipantMeta {
  try {
    return JSON.parse(metadata || '{}') || {};
  } catch {
    return {};
  }
}

export function participantRole(p: Participant): string {
  return participantMeta(p).role || 'guest';
}

// Кто этот участник на самом деле: userId из метаданных. identity не годится:
// у каждого входа она своя
export function participantUid(p: Participant): string {
  return participantMeta(p).uid || p.identity;
}

// RoomService LiveKit: сервер связи проксирует /twirp/* и принимает токен судьи
// с правом roomAdmin. Мьют и метаданные; удаление — через ban.php
export class RoomService {
  constructor(
    private server: string,
    private token: string,
    private room: string,
  ) {}

  mute(identity: string, trackSid: string, muted: boolean): Promise<void> {
    return this.call('MutePublishedTrack', { identity, trackSid, muted });
  }

  // Метаданные заменяются целиком: передавать полный объект
  updateMetadata(identity: string, meta: ParticipantMeta): Promise<void> {
    return this.call('UpdateParticipant', { identity, metadata: JSON.stringify(meta) });
  }

  private async call(method: string, body: object): Promise<void> {
    const res = await fetch(`${serverBase(this.server)}/twirp/livekit.RoomService/${method}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ room: this.room, ...body }),
    });
    if (!res.ok) throw new Error(`Сервер видеосвязи ответил ${res.status}`);
  }
}

// Черный список комнаты живет на сервере связи: ban.php рядом с token.php
export type BanAction = 'who' | 'list' | 'ban' | 'unban';

export async function callBan(
  server: string,
  ticket: string,
  action: BanAction,
  uid = '',
): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(`${serverBase(server)}/ban.php`, {
      method: 'POST',
      body: new URLSearchParams({ ticket, action, uid }),
    });
  } catch {
    throw new Error('Сервер видеосвязи недоступен');
  }
  if (!res.ok) throw new Error(`Сервер видеосвязи ответил ${res.status}`);
  return res.json().catch(() => null);
}

// Ответ ban.php на `list`: комната и забаненные `{ [userId]: имя }`
type BanListResponse = { room?: string; banned?: Record<string, string> };

export function parseBanList(data: unknown): string[] {
  const banned = (data as BanListResponse | null)?.banned;
  return banned && typeof banned === 'object' ? Object.keys(banned) : [];
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Адрес сервера из claim `aud`: приватный сервер приходит без схемы, иначе fetch
// ушел бы относительно адреса страницы
function serverBase(server: string): string {
  const base = server.replace(/\/+$/, '');
  return /^https?:\/\//.test(base) ? base : `https://${base}`;
}

// Причина ошибки LiveKit в одну строку: у ConnectionError есть код причины
function describeError(e: unknown): string {
  const reason = (e as { reason?: unknown } | null)?.reason;
  const message = e instanceof Error ? e.message : String(e);
  return reason === undefined ? message : `${message} (${String(reason)})`;
}
